import React, { useState } from 'react';
import axiosInstance from '../utils/axiosConfig';

const NotificationChoiceModal = ({ 
    isOpen, 
    onClose, 
    notificationType, // 'new-file' | 'arrival-update' | 'inspection' | 'gate-pass'
    notificationData,
    onComplete 
}) => {
    const [isSending, setIsSending] = useState(false);
    const [result, setResult] = useState(null); // null | { success: boolean, message: string, sentMessage: string }

    if (!isOpen) return null;

    const getNotificationTitle = () => {
        switch (notificationType) {
            case 'new-file':
                return 'إشعار فتح الملف';
            case 'arrival-update':
                return 'إشعار تاريخ الوصول';
            case 'inspection':
                return 'إشعار موعد الفحص';
            case 'gate-pass':
                return 'إشعار الجيت باس';
            default:
                return 'إشعار';
        }
    };

    const getNotificationIcon = () => {
        switch (notificationType) {
            case 'new-file':
                return (
                    <svg style={{ width: '48px', height: '48px' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd" />
                    </svg>
                );
            case 'arrival-update':
                return (
                    <svg style={{ width: '48px', height: '48px' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                );
            case 'inspection':
                return (
                    <svg style={{ width: '48px', height: '48px' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                );
            case 'gate-pass':
                return (
                    <svg style={{ width: '48px', height: '48px' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                );
            default:
                return null;
        }
    };

    const buildMessagePreview = () => {
        const { file_num, container_numbers, expected_arrival_date, file_cost, goods_description, inspection_date, inspection_time, custom_dec_num, package_count } = notificationData || {};
        const containersStr = container_numbers?.length > 0 ? container_numbers.join('\n') : 'لا توجد حاويات';
        
        switch (notificationType) {
            case 'new-file':
                return `تحية طيبة،
نفيدكم بأنه تم فتح ملف تخليص
حسب التفاصيل التالية:

رقم ملف التخليص:
${file_num || 'غير محدد'}

رقم الحاوية:
${containersStr}

الوصول المتوقع:
${expected_arrival_date || 'غير محدد'}

حسبة الملف (تقدير أولي):
${file_cost ? parseFloat(file_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'غير محدد'}

وصف البضاعة:
${goods_description || 'غير محدد'}

نصار للخدمات التجارية`;

            case 'arrival-update':
                return `تحية طيبة،
نفيدكم بأنه تم تحديث تاريخ الوصول المتوقع
حسب التفاصيل التالية:

رقم ملف التخليص:
${file_num || 'غير محدد'}

رقم الحاوية:
${containersStr}

الوصول المتوقع:
${expected_arrival_date || 'غير محدد'}

حسبة الملف (تقدير أولي):
${file_cost ? parseFloat(file_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'غير محدد'}

وصف البضاعة:
${goods_description || 'غير محدد'}

نصار للخدمات التجارية`;

            case 'inspection':
                const datetimeStr = inspection_date && inspection_time ? `${inspection_date} - ${inspection_time}` : 'غير محدد';
                return `تحية طيبة،
نفيدكم بأنه تم تعيين موعد فحص
حسب التفاصيل التالية:

رقم ملف التخليص:
${file_num || 'غير محدد'}

رقم الحاوية:
${containersStr}

التاريخ والوقت:
${datetimeStr}

وصف البضاعة:
${goods_description || 'غير محدد'}

نصار للخدمات التجارية`;

            case 'gate-pass':
                return `تحية طيبة،
نفيدكم بأنه تم تحرير بضاعتكم
حسب التفاصيل التالية:

رقم ملف التخليص:
${file_num || 'غير محدد'}

رقم الحاوية:
${containersStr}

رقم البيان الجمركي:
${custom_dec_num || 'غير محدد'}

عدد الباكج:
${package_count || 'غير محدد'}

وصف البضاعة:
${goods_description || 'غير محدد'}

نصار للخدمات التجارية`;

            default:
                return '';
        }
    };

    const getApiEndpoint = () => {
        switch (notificationType) {
            case 'new-file':
                return '/wa-notify/new-file/';
            case 'arrival-update':
                return '/wa-notify/arrival-update/';
            case 'inspection':
                return '/wa-notify/inspection/';
            case 'gate-pass':
                return '/wa-notify/gate-pass/';
            default:
                return null;
        }
    };

    const handleSend = async () => {
        setIsSending(true);
        try {
            const endpoint = getApiEndpoint();
            if (!endpoint) {
                setResult({ success: false, message: 'نوع الإشعار غير معروف', sentMessage: '' });
                return;
            }

            const response = await axiosInstance.post(endpoint, notificationData);
            setResult({
                success: response.data.success,
                subscriptionExpired: response.data.subscription_expired || false,
                message: response.data.message,
                sentMessage: response.data.sent_message || ''
            });
        } catch (err) {
            console.error('Error sending notification:', err);
            setResult({
                success: false,
                subscriptionExpired: false,
                message: 'فشل في إرسال الإشعار',
                sentMessage: ''
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleSkip = () => {
        onComplete?.();
        onClose();
    };

    const handleResultClose = () => {
        onComplete?.();
        onClose();
    };

    // Show result modal if we have a result
    if (result) {
        return (
            <div className="modal-overlay" onClick={handleResultClose} style={{ zIndex: 2200 }}>
                <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header" style={{ 
                        background: result.success 
                            ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                            : result.subscriptionExpired 
                                ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)'
                                : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
                    }}>
                        <h3 className="modal-title" style={{ color: 'white' }}>
                            {result.success ? 'تم إرسال الإشعار' : 'إشعار الواتساب'}
                        </h3>
                        <button 
                            type="button" 
                            className="modal-close" 
                            onClick={handleResultClose}
                            style={{ color: 'white' }}
                        >
                            <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: result.success 
                                    ? '#d4edda' 
                                    : result.subscriptionExpired 
                                        ? '#fff3cd'
                                        : '#f8d7da',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 1.5rem'
                            }}>
                                {result.success ? (
                                    <svg style={{ width: '48px', height: '48px', color: '#28a745' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                ) : result.subscriptionExpired ? (
                                    <svg style={{ width: '48px', height: '48px', color: '#ffc107' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg style={{ width: '48px', height: '48px', color: '#dc3545' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </div>
                            
                            <h3 style={{ 
                                color: result.success 
                                    ? '#155724' 
                                    : result.subscriptionExpired 
                                        ? '#856404'
                                        : '#721c24',
                                marginBottom: '1rem',
                                fontSize: '1.25rem'
                            }}>
                                {result.success 
                                    ? 'تم إرسال رسالة الواتساب بنجاح' 
                                    : result.subscriptionExpired 
                                        ? 'تعذر إرسال الرسالة - انتهى الاشتراك'
                                        : 'فشل في إرسال الرسالة'
                                }
                            </h3>
                            
                            {result.sentMessage && (
                                <div style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '10px',
                                    padding: '1rem',
                                    marginTop: '1rem',
                                    textAlign: 'right',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                }}>
                                    <p style={{ 
                                        margin: 0, 
                                        fontSize: '0.9rem', 
                                        fontWeight: '600', 
                                        color: '#03428d',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {result.success ? 'الرسالة المرسلة:' : 'الرسالة التي كان يفترض إرسالها:'}
                                    </p>
                                    <pre style={{ 
                                        margin: 0, 
                                        whiteSpace: 'pre-wrap', 
                                        fontFamily: 'inherit',
                                        fontSize: '0.85rem',
                                        color: '#495057',
                                        lineHeight: '1.6'
                                    }}>
                                        {result.sentMessage}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer" style={{ justifyContent: 'center' }}>
                        <button 
                            type="button" 
                            className="btn btn-accent btn-large"
                            onClick={handleResultClose}
                        >
                            <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>حسناً</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show choice modal
    return (
        <div className="modal-overlay" onClick={handleSkip} style={{ zIndex: 2200 }}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px', width: '95vw' }}>
                <div className="modal-header" style={{ 
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
                }}>
                    <h3 className="modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        {getNotificationTitle()}
                    </h3>
                    <button 
                        type="button" 
                        className="modal-close" 
                        onClick={handleSkip}
                        style={{ color: 'white' }}
                    >
                        <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
                
                <div className="modal-body">
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#e8f5e9',
                            color: '#25D366',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 1.5rem'
                        }}>
                            {getNotificationIcon()}
                        </div>
                        
                        <h3 style={{ color: '#2c3e50', marginBottom: '0.5rem', fontSize: '1.2rem' }}>
                            هل تريد إرسال إشعار واتساب للزبون؟
                        </h3>
                        <p style={{ color: '#6c757d', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                            سيتم إرسال الرسالة التالية إلى رقم الزبون
                        </p>
                        
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '10px',
                            padding: '1rem',
                            textAlign: 'right',
                            maxHeight: '250px',
                            overflowY: 'auto'
                        }}>
                            <pre style={{ 
                                margin: 0, 
                                whiteSpace: 'pre-wrap', 
                                fontFamily: 'inherit',
                                fontSize: '0.85rem',
                                color: '#495057',
                                lineHeight: '1.6'
                            }}>
                                {buildMessagePreview()}
                            </pre>
                        </div>
                    </div>
                </div>
                
                <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                    <button 
                        type="button" 
                        className="btn"
                        onClick={handleSend}
                        disabled={isSending}
                        style={{ 
                            flex: 1,
                            maxWidth: '180px',
                            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                            color: 'white',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        {isSending ? (
                            <>
                                <div style={{ width: '20px', height: '20px', border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                <span>جاري الإرسال...</span>
                            </>
                        ) : (
                            <>
                                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                                </svg>
                                <span>إرسال</span>
                            </>
                        )}
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-outline"
                        onClick={handleSkip}
                        disabled={isSending}
                        style={{ 
                            flex: 1,
                            maxWidth: '180px',
                            background: 'white',
                            color: '#6c757d',
                            border: '2px solid #e1e8ed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span>تخطي</span>
                    </button>
                </div>
                
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
};

export default NotificationChoiceModal;
